import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { Button } from "@cieda/djayma-ui";
import { useStockHistory } from "@/hooks/admin/useStockHistory";
import { StockHistoryRow } from "./StockHistoryRow";

interface StockHistoryListProps {
  readonly productId: string;
}

/**
 * Paginated (infinite-query) list of inventory movements, newest first.
 * Handles loading / empty / error / success states per UI standard.
 */
export function StockHistoryList({ productId }: StockHistoryListProps) {
  const { t } = useTranslation("admin");
  const {
    data,
    isLoading,
    isError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useStockHistory(productId);

  const allItems = data?.pages.flatMap((page) => page.items) ?? [];

  if (isLoading) {
    return (
      <div
        className="flex items-center justify-center py-8"
        role="status"
        aria-label={t("common.loading", { defaultValue: "Chargement" })}
      >
        <Loader2 className="h-5 w-5 animate-spin text-stone-400" />
      </div>
    );
  }

  if (isError) {
    return (
      <p className="py-8 text-center text-sm text-red-500">
        {t("products.error_boundary.message")}
      </p>
    );
  }

  if (allItems.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-stone-400">
        {t("products.stock.history_empty")}
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-medium text-stone-700">
        {t("products.stock.history_title")}
      </h3>
      <div className="divide-y divide-stone-100">
        {allItems.map((item) => (
          <StockHistoryRow key={item.id} item={item} />
        ))}
      </div>
      {hasNextPage && (
        <Button
          variant="ghost"
          size="sm"
          className="w-full"
          onClick={() => void fetchNextPage()}
          disabled={isFetchingNextPage}
          type="button"
        >
          {isFetchingNextPage ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            t("products.stock.history_full_link")
          )}
        </Button>
      )}
    </div>
  );
}
